import { type BlockConfig, iconField, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `heroProtect` block.
 *
 * Field tree mirrors the CMS-authorable structural copy in
 * `./hero-protect.tsx` (headline, CTAs, eyebrow). The 3 floating cards
 * graphic on the right side of the hero is HARDCODED in the React
 * component because the cards are static decorative content (illustrative
 * examples like "Your email found in LinkedIn breach"). The CMS owns the
 * structured content; the component owns the visual decoration.
 *
 * At Next.js cutover, this sidecar moves to
 * `apps/marketing/payload/blocks/hero-protect.ts` via the same 5-line
 * `cp + sed` documented in `./_types.ts`.
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `hero-protect.tsx` / `hero-protect.payload.ts`
 *   React component export:  `HeroProtect`
 *   Payload `slug`:          `heroProtect`
 *   Payload `interfaceName`: `HeroProtectBlock`
 */

export const HeroProtectBlock: BlockConfig = {
  slug: 'heroProtect',
  interfaceName: 'HeroProtectBlock',
  labels: {
    singular: 'Protect Hero',
    plural: 'Protect Hero Blocks',
  },
  fields: [
    {
      name: 'eyebrow',
      type: 'text',
      label: 'Eyebrow / Badge text',
    },
    {
      name: 'eyebrowVariant',
      type: 'select',
      defaultValue: 'badge',
      label: 'Eyebrow style',
      options: [
        { label: 'Section badge', value: 'badge' },
        { label: 'Pill', value: 'pill' },
      ],
    },
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'headlineEmphasis',
      type: 'text',
      label: 'Headline emphasis',
      admin: {
        description:
          'Optional phrase rendered in the primary accent color after the headline. Example: "Stay Protected." on /protect.',
      },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 2 },
    },
    {
      name: 'primaryCta',
      type: 'group',
      label: 'Primary CTA',
      fields: [
        { name: 'text', type: 'text', label: 'Button text' },
        linkField({ name: 'link', label: 'Button URL' }),
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
        {
          ...iconField({
            name: 'iconRight',
            label: 'Right-side icon (lucide-react name)',
          }),
        },
        {
          name: 'variant',
          type: 'select',
          defaultValue: 'default',
          label: 'Button variant',
          options: [
            { label: 'Default (filled)', value: 'default' },
            { label: 'Outline', value: 'outline' },
            { label: 'Ghost', value: 'ghost' },
          ],
        },
      ],
    },
    {
      name: 'secondaryCta',
      type: 'group',
      label: 'Secondary CTA',
      fields: [
        { name: 'text', type: 'text', label: 'Button text' },
        linkField({ name: 'link', label: 'Button URL' }),
        {
          name: 'external',
          type: 'checkbox',
          defaultValue: false,
          label: 'External link?',
        },
        {
          ...iconField({
            name: 'iconRight',
            label: 'Right-side icon (lucide-react name)',
          }),
        },
        {
          name: 'variant',
          type: 'select',
          defaultValue: 'outline',
          label: 'Button variant',
          options: [
            { label: 'Default (filled)', value: 'default' },
            { label: 'Outline', value: 'outline' },
            { label: 'Ghost', value: 'ghost' },
          ],
        },
      ],
    },
    {
      name: 'extraContent',
      type: 'array',
      label: 'Extra content (icon + text rows below CTAs)',
      labels: { singular: 'Item', plural: 'Items' },
      maxRows: 4,
      fields: [
        {
          ...iconField({
            name: 'icon',
            label: 'Icon (lucide-react name)',
          }),
        },
        { name: 'text', type: 'text', required: true, label: 'Text' },
      ],
    },
  ],
};

export default HeroProtectBlock;
